import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Github, Cloud, Mail, CheckCircle, XCircle, Loader2, Shield, AlertCircle, ExternalLink, Copy, Building2 } from 'lucide-react';
import Header from './Header';
import GwMonitoringHealth from './GwMonitoringHealth';
import { parseServerDate } from '../utils/dateFormatter';

const API = '';

const TechnologyConnections = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [technologies, setTechnologies] = useState([]);
  const [userConnections, setUserConnections] = useState([]);
  const [evidenceHistory, setEvidenceHistory] = useState([]);
  const [gwOverview, setGwOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [techToDisconnect, setTechToDisconnect] = useState(null);
  const [collectingTechId, setCollectingTechId] = useState(null);
  const [collectProgress, setCollectProgress] = useState(null); // { step, message, current_repo_index, total_repos, current_repo_name }
  
  // GitHub connection form state
  const [githubToken, setGithubToken] = useState('');
  const [githubUsername, setGithubUsername] = useState('');
  const [githubOrg, setGithubOrg] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Google Workspace connection form state
  const [gwAdminEmail, setGwAdminEmail] = useState('');
  const [gwCustomerDomain, setGwCustomerDomain] = useState('');
  const [gwNotes, setGwNotes] = useState('');
  const [gwSetupInfo, setGwSetupInfo] = useState(null);
  const [gwVerifying, setGwVerifying] = useState(false);

  // Microsoft 365 connection form state (in-app modal, same as GW/GitHub)
  const [m365Form, setM365Form] = useState({ tenant_id: '', client_id: '', client_secret: '' });
  const [m365SetupInfo, setM365SetupInfo] = useState(null);
  const [m365ShowSteps, setM365ShowSteps] = useState(true);

  // Edit-admin-email mini-flow on the connected GW card. Lets the user
  // correct a typo (e.g. `sri@fastcolab.com` → `sri@fastcollab.com`)
  // without disconnecting the whole connection.
  const [gwEditEmailOpen, setGwEditEmailOpen] = useState(false);
  const [gwEditEmailValue, setGwEditEmailValue] = useState('');
  const [gwEditEmailSaving, setGwEditEmailSaving] = useState(false);

  useEffect(() => {
    if (!user || !currentWorkspace) {
      navigate('/login');
      return;
    }
    
    loadData();
  }, [user, currentWorkspace]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Core data needed to render the page — fetch in parallel so total
      // latency is the slowest single call, not the sum of all of them.
      const [techResponse, connResponse, evidenceResponse] = await Promise.all([
        axios.get(`${API}/api/technologies/available`),
        axios.get(`${API}/api/technologies/connections/${user.id}/${currentWorkspace.id}`),
        axios.get(`${API}/api/technologies/evidence-history/${user.id}/${currentWorkspace.id}`),
      ]);
      setTechnologies(techResponse.data.technologies);
      setUserConnections(connResponse.data.connections);
      setEvidenceHistory(evidenceResponse.data.history);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load technology connections');
    } finally {
      // Render the page as soon as core data is in — the GW posture rollup
      // below is optional and loads in the background.
      setLoading(false);
    }

    // Domain-native GW overview (users / groups / OUs / admins / findings /
    // score / grade / top findings). Fire-and-forget so it never blocks the
    // page from rendering — the card has a graceful fallback if it fails.
    try {
      const gwResp = await axios.get(
        `${API}/api/posture/google-workspace/overview`,
        { params: { workspace_id: currentWorkspace.id } }
      );
      setGwOverview(gwResp.data || null);
    } catch (e) {
      setGwOverview(null);
    }
  };

  const getTechIcon = (techId) => {
    switch (techId) {
      case 'github':
        return <Github className="w-6 h-6" />;
      case 'aws':
        return <Cloud className="w-6 h-6" />;
      case 'google-workspace':
        return <Mail className="w-6 h-6" />;
      case 'microsoft-365':
        return <Building2 className="w-6 h-6" />;
      default:
        return <Shield className="w-6 h-6" />;
    }
  };

  const isConnected = (techId) => {
    // The available-technology id uses hyphens (e.g., 'google-workspace')
    // but the backend stores connections with the underscore form
    // (e.g., 'google_workspace'). Normalize before comparing so the
    // connected-state badge / "Collect Evidence" button render correctly
    // immediately after a successful connect.
    const storageId = (techId || '').replace(/-/g, '_');
    return userConnections.some(conn => conn.technology === techId || conn.technology === storageId);
  };

  // Returns the connection status (e.g., "active" | "pending_admin_approval")
  // for the workspace's row of this technology, or null if no row exists.
  const getConnectionStatus = (techId) => {
    const storageId = (techId || '').replace(/-/g, '_');
    const conn = userConnections.find(c => c.technology === techId || c.technology === storageId);
    return conn ? conn.status : null;
  };

  // Returns the verification error message (if any) for the workspace's
  // pending row of this technology.
  const getVerificationError = (techId) => {
    const storageId = (techId || '').replace(/-/g, '_');
    const conn = userConnections.find(c => c.technology === techId || c.technology === storageId);
    return conn?.last_verification?.error_msg || null;
  };

  // Returns true when the connection is active but missing one or more
  // newer scopes (set during the latest `/verify` probe). Drives the
  // "Re-authorize for new posture checks" banner.
  const getScopesPendingReauth = (techId) => {
    const storageId = (techId || '').replace(/-/g, '_');
    const conn = userConnections.find(c => c.technology === techId || c.technology === storageId);
    return Boolean(conn?.scopes_pending_reauth);
  };

  const handleConnectClick = async (tech) => {
    if (tech.status !== 'available') {
      toast.info(`${tech.name} integration coming soon!`);
      return;
    }

    setSelectedTech(tech);
    if (tech.id === 'google-workspace' && !gwSetupInfo) {
      try {
        const r = await axios.get(`${API}/api/technologies/google-workspace/setup-info`);
        setGwSetupInfo(r.data);
      } catch (e) {
        console.error('Failed to load Google Workspace setup info', e);
        setGwSetupInfo({ client_id: '', scopes: [], instructions: [], mode: 'mock' });
      }
    }
    if (tech.id === 'microsoft-365' && !m365SetupInfo) {
      try {
        const r = await axios.get(`${API}/api/technologies/microsoft-365/setup-info`);
        setM365SetupInfo(r.data);
      } catch (e) {
        console.error('Failed to load Microsoft 365 setup info', e);
        setM365SetupInfo({ required_permissions: [], instructions: [], notes: '', portal_url: '' });
      }
    }
    setConnectDialogOpen(true);
  };

  const handleMicrosoft365Connect = async () => {
    if (!m365Form.tenant_id.trim() || !m365Form.client_id.trim() || !m365Form.client_secret.trim()) {
      toast.error('Tenant ID, Client ID and Client Secret are all required');
      return;
    }
    try {
      setConnecting(true);
      const resp = await axios.post(`${API}/api/technologies/connect/microsoft-365`, {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        tenant_id: m365Form.tenant_id.trim(),
        client_id: m365Form.client_id.trim(),
        client_secret: m365Form.client_secret,
        verify_now: true,
      });
      setConnectDialogOpen(false);
      setM365Form({ tenant_id: '', client_id: '', client_secret: '' });
      if (resp.data?.status === 'active') {
        toast.success(`Microsoft 365 connected — ${resp.data.tenant_name || 'tenant verified'}. Collecting evidence…`);
        // Route to the detail page and auto-kick the full collection + posture
        // pipeline (same one-click behaviour as Google Workspace / GitHub).
        navigate('/technologies/microsoft-365?autocollect=1');
        return;
      }
      const errMsg = resp.data?.last_verification?.error_msg;
      toast.warning(errMsg
        ? `Saved as pending — verification failed: ${errMsg}`
        : 'Saved — verification pending. Check permissions & admin consent.', { duration: 8000 });
      await loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to connect Microsoft 365');
    } finally {
      setConnecting(false);
    }
  };

  const handleGoogleWorkspaceConnect = async (verifyNow) => {
    if (!gwAdminEmail.trim() || !gwCustomerDomain.trim()) {
      toast.error('Admin email and primary domain are required');
      return;
    }
    try {
      setConnecting(true);
      const resp = await axios.post(`${API}/api/technologies/connect/google-workspace`, {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        admin_email: gwAdminEmail.trim(),
        customer_domain: gwCustomerDomain.trim(),
        notes: gwNotes.trim(),
        verify_now: !!verifyNow,
      });
      const status = resp.data?.status;
      if (status === 'active') {
        toast.success('Google Workspace connected and verified.');
      } else {
        const errMsg = resp.data?.verification?.error_msg;
        if (verifyNow && errMsg) {
          toast.warning(`Saved as pending — verification failed: ${errMsg}`, { duration: 8000 });
        } else {
          toast.success('Saved — waiting for super admin to complete Domain-Wide Delegation.');
        }
      }
      setConnectDialogOpen(false);
      setGwAdminEmail('');
      setGwCustomerDomain('');
      setGwNotes('');
      await loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to connect Google Workspace');
    } finally {
      setConnecting(false);
    }
  };

  const handleVerifyGoogleWorkspace = async () => {
    try {
      setGwVerifying(true);
      const resp = await axios.post(`${API}/api/technologies/google-workspace/verify`, {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
      });
      if (resp.data?.success) {
        toast.success('Google Workspace verified — connection is now active.');
      } else {
        const msg = resp.data?.verification?.error_msg || resp.data?.message || 'Verification failed';
        toast.error(msg, { duration: 8000 });
      }
      await loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Verification request failed');
    } finally {
      setGwVerifying(false);
    }
  };

  // Open the edit-admin-email dialog seeded with the current value (if any).
  const openGwEditEmail = () => {
    const existing = userConnections.find(
      c => c.technology === 'google_workspace' || c.technology === 'google-workspace'
    );
    const currentEmail =
      existing?.credentials?.admin_email
      || existing?.admin_email
      || '';
    setGwEditEmailValue(currentEmail);
    setGwEditEmailOpen(true);
  };

  const handleGwUpdateAdminEmail = async () => {
    const newEmail = gwEditEmailValue.trim();
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Enter a valid email (e.g. admin@yourdomain.com)');
      return;
    }
    try {
      setGwEditEmailSaving(true);
      const resp = await axios.post(
        `${API}/api/technologies/google-workspace/update-admin-email`,
        {
          user_id: user.id,
          workspace_id: currentWorkspace.id,
          admin_email: newEmail,
        }
      );
      const data = resp.data || {};
      // Domain-mismatch warning is informational — surface it but don't block,
      // since aliases on a secondary verified domain are legitimate.
      if (data.domain_mismatch) {
        toast.warning(
          `Note: the email domain doesn't match the customer's primary domain (${data.customer_domain}). ` +
          `If this is intentional (alias on a secondary verified domain), ignore this. Otherwise, double-check the spelling.`,
          { duration: 10000 }
        );
      }
      if (data.success) {
        toast.success(data.message || 'Admin email updated and verified.');
      } else {
        toast.error(data.message || 'Admin email saved, but DWD probe failed.', { duration: 10000 });
      }
      setGwEditEmailOpen(false);
      await loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update admin email');
    } finally {
      setGwEditEmailSaving(false);
    }
  };

  const handleGitHubConnect = async () => {
    if (!githubToken.trim()) {
      toast.error('Please enter your GitHub token');
      return;
    }
    
    try {
      setConnecting(true);
      
      // Build payload without null values
      const payload = {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        github_token: githubToken
      };
      
      if (githubUsername && githubUsername.trim()) {
        payload.username = githubUsername;
      }
      
      if (githubOrg && githubOrg.trim()) {
        payload.organization = githubOrg;
      }
      
      const response = await axios.post(`${API}/api/technologies/connect/github`, payload);
      
      toast.success('GitHub connected successfully!');
      setConnectDialogOpen(false);
      setGithubToken('');
      setGithubUsername('');
      setGithubOrg('');
      
      // Reload connections
      await loadData();
      
    } catch (error) {
      console.error('Connection failed:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to connect GitHub';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // If detail is a string, use it
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // If detail is an array (Pydantic validation errors)
        else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg).join(', ');
        }
        // If it's a validation error object
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setConnecting(false);
    }
  };

  const handleCollectEvidence = async (techId) => {
    // Debug correlation id so a single click is easy to trace across log lines.
    const debugId = `evt-${techId}-${Date.now().toString(36)}`;
    const debugStart = performance.now();
    /* eslint-disable no-console */
    console.group(`%c[CollectEvidence:${techId}] ${debugId}`, 'color:#0f766e;font-weight:600');
    console.info('🔍 Debug trace for evidence collection. Copy this whole group when reporting issues.');
    console.debug('env', {
      apiBase: API,
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
      workspaceId: currentWorkspace?.id,
      workspaceName: currentWorkspace?.name,
      techId,
      ts: new Date().toISOString(),
    });

    try {
      setCollectingTechId(techId);
      setCollectProgress({ step: 'starting', message: 'Starting evidence collection…' });

      const startUrl = `${API}/api/technologies/collect-evidence/${techId}`;
      const startBody = {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        customer_name: currentWorkspace.name || 'Customer',
        customer_email: user.email,
        customer_organization: currentWorkspace.name || 'Organization',
        contact_person: user.email,
      };
      console.debug('▶ POST start', { url: startUrl, body: startBody });

      const startResp = await axios.post(startUrl, startBody);
      console.debug('◀ start response', {
        httpStatus: startResp.status,
        data: startResp.data,
      });

      const jobId = startResp.data?.job_id;
      if (!jobId) {
        console.error('❌ no job_id in start response — server contract broken', startResp.data);
        throw new Error('Server did not return a job_id');
      }
      console.info(`✅ job started: ${jobId}`);

      toast.info('Collection started — you can keep working; this may take a few minutes.', { duration: 5000 });

      // Poll for completion
      const maxMs = 30 * 60 * 1000; // 30 min upper bound
      const startedAt = Date.now();
      let finalJob = null;
      let pollCount = 0;
      let lastStep = null;

      while (Date.now() - startedAt < maxMs) {
        await new Promise(r => setTimeout(r, 3000));
        pollCount += 1;
        const pollUrl = `${API}/api/technologies/evidence-jobs/${jobId}`;
        let jobResp;
        try {
          jobResp = await axios.get(pollUrl);
        } catch (pollErr) {
          console.warn(`⚠ poll #${pollCount} failed (will retry)`, {
            url: pollUrl,
            httpStatus: pollErr?.response?.status,
            data: pollErr?.response?.data,
            message: pollErr?.message,
          });
          continue;
        }
        const job = jobResp.data;
        const step = job?.progress?.step || job?.status;
        // Log every poll at debug level, but call out step changes at info level
        // so the console isn't a wall of dupes.
        if (step !== lastStep) {
          console.info(`📡 poll #${pollCount}  status=${job?.status}  step=${step}  msg="${job?.progress?.message || ''}"`);
          lastStep = step;
        } else {
          console.debug(`poll #${pollCount}`, {
            status: job?.status,
            progress: job?.progress,
            updated_at: job?.updated_at,
          });
        }
        setCollectProgress(job.progress || { step: job.status, message: '' });
        if (job.status === 'completed' || job.status === 'failed') {
          finalJob = job;
          break;
        }
      }

      if (!finalJob) {
        console.error(`❌ timeout — no terminal status after ${pollCount} polls / ${Math.round((Date.now() - startedAt) / 1000)}s`);
        toast.error('Collection is still running after 30 minutes. Check evidence history later.');
        return;
      }

      const elapsedSec = Math.round((Date.now() - startedAt) / 1000);
      console.info(`🏁 final status=${finalJob.status} after ${pollCount} polls / ${elapsedSec}s`);
      // Flat one-liner so the user can copy without expanding the Object.
      console.info(
        `🧾 finalJob (inline) — job_id=${finalJob.job_id} status=${finalJob.status} ` +
        `technology=${finalJob.technology} evidence_id=${finalJob.evidence_id || '—'} ` +
        `error=${finalJob.error ? JSON.stringify(finalJob.error) : 'null'} ` +
        `started_at=${finalJob.started_at} completed_at=${finalJob.completed_at || '—'} ` +
        `progress.step=${finalJob?.progress?.step || '—'} progress.msg="${finalJob?.progress?.message || ''}"`
      );
      console.debug('finalJob (structured)', finalJob);

      if (finalJob.status === 'failed') {
        console.error('❌ collection failed', {
          error: finalJob.error,
          progress: finalJob.progress,
        });
        toast.error(`Evidence collection failed: ${finalJob.error || 'unknown error'}`);
        return;
      }

      // status === 'completed'
      const summary = finalJob.summary || {};
      // 🚨 Highlight mode so the user can immediately see whether they got
      //    real GW data or canned mock data. After the Feb-2026 mock-removal
      //    this should always be "live"; if it's "mock" the server is still
      //    running the old code (likely a redeploy is needed in production).
      const collectionMode = summary.mode || finalJob?.progress?.mode || 'unknown';
      if (collectionMode === 'mock') {
        console.warn(`⚠️ COLLECTION RAN IN MOCK MODE — server returned canned data, NOT real ${techId} evidence. ` +
          `Most likely: the backend hasn't been redeployed with the mock-removal patch, OR the service-account JSON is unset. ` +
          `Check Super Admin → Environment Settings → Integrations.`);
      } else {
        console.info(`✅ collection mode: ${collectionMode}`);
      }
      console.debug('summary', summary);

      // GW-specific debug surface: counts and directory_sync stats.
      if (techId === 'google-workspace') {
        // Two log lines on purpose:
        //  (1) a flat one-line scalar dump — visible without expanding the
        //      object in DevTools. Copy-paste-friendly for bug reports.
        //  (2) the structured object for click-to-expand inspection.
        const snap = {
          mode: collectionMode,
          users_total: summary.total_users ?? 0,
          active_users: summary.active_users ?? 0,
          suspended_users: summary.suspended_users ?? 0,
          admins: summary.admins ?? 0,
          users_with_2sv_enrolled: summary.users_with_2sv_enrolled ?? 0,
          users_with_2sv_enforced: summary.users_with_2sv_enforced ?? 0,
          groups_total: summary.groups_total ?? 0,
          org_units_total: summary.org_units_total ?? 0,
          roles_total: summary.roles_total ?? 0,
          role_assignments_total: summary.role_assignments_total ?? 0,
          domains_total: summary.domains_total ?? 0,
          warnings_count: summary.warnings_count ?? 0,
          customer_domain: summary.customer_domain || '—',
          primary_admin_email: summary.primary_admin_email || '—',
          evidence_id: summary.evidence_id || '—',
        };
        console.info(
          `📊 GW snapshot (inline) — mode=${snap.mode} users=${snap.users_total} ` +
          `active=${snap.active_users} suspended=${snap.suspended_users} admins=${snap.admins} ` +
          `2sv_enrolled=${snap.users_with_2sv_enrolled} 2sv_enforced=${snap.users_with_2sv_enforced} ` +
          `groups=${snap.groups_total} org_units=${snap.org_units_total} ` +
          `roles=${snap.roles_total} role_assignments=${snap.role_assignments_total} ` +
          `domains=${snap.domains_total} warnings=${snap.warnings_count} ` +
          `domain="${snap.customer_domain}" admin="${snap.primary_admin_email}" ` +
          `evidence_id="${snap.evidence_id}"`
        );
        // Hard warning when the live mode succeeded but every count is 0 —
        // means the service-account JSON is configured AND DWD probe passed,
        // but the impersonated admin email has no access to read users/groups.
        // Most common cause: the connection's admin_email is not a real
        // super-admin in the customer's Workspace, OR DWD scopes weren't
        // saved with this Client ID. Surface the actionable fix.
        if (
          collectionMode === 'live' &&
          snap.users_total === 0 &&
          snap.groups_total === 0 &&
          snap.admins === 0
        ) {
          console.error(
            '🚨 Live collection returned ZERO users / groups / admins. ' +
            'The service account authenticated, but the impersonated admin ' +
            'email has no Directory read access. Verify:\n' +
            '  (a) the admin email on the connection is a real Workspace super admin\n' +
            '  (b) the customer has saved DWD with THIS Client ID and ALL the readonly scopes\n' +
            '  (c) wait 1–5 min after DWD save for propagation, then re-collect\n' +
            'See Super Admin → Integrations → "How to set this up" for the scope list.'
          );
        }
        console.debug('snap (structured)', snap);
        if (summary.warnings_count > 0) {
          console.warn(
            `⚠ ${summary.warnings_count} warning(s) recorded by the collector — ` +
            'these are individual endpoint failures inside the live run. ' +
            'Check backend logs (/var/log/supervisor/backend.err.log) for ' +
            '"[gw_collect job_id=' + (finalJob.job_id || '?') + ']" to see each one.'
          );
          // Per-endpoint errors are surfaced from the backend (see
          // technology_service.collect_google_workspace_evidence). If the
          // backend hasn't been redeployed with that patch this array
          // will be empty / undefined — and that itself is the signal to
          // redeploy.
          const errs = Array.isArray(summary.errors) ? summary.errors : [];
          if (errs.length === 0) {
            console.warn(
              '   (no per-endpoint error detail in summary — backend likely ' +
              'pre-dates the errors-surfacing patch, redeploy and retry to see them)'
            );
          } else {
            errs.forEach((e, i) => {
              console.error(`   [${i + 1}/${errs.length}] ${e.where || '?'} → ${e.error || '?'}`);
            });
          }
        }
        if (summary.directory_sync) {
          const ds = summary.directory_sync;
          console.info(
            `🗂 directory_sync — synced=${ds.synced ?? 0} updated=${ds.updated ?? 0} ` +
            `marked_removed=${ds.marked_removed ?? 0} total_in_payload=${ds.total_in_payload ?? 0} ` +
            (ds.groups ? `groups{synced=${ds.groups.synced ?? 0} updated=${ds.groups.updated ?? 0}} ` : '') +
            (ds.org_units ? `org_units{synced=${ds.org_units.synced ?? 0} updated=${ds.org_units.updated ?? 0}} ` : '') +
            (ds.role_assignments ? `roles{synced=${ds.role_assignments.synced ?? 0}} ` : '') +
            (ds.policies ? `policies{mode=${ds.policies.mode || '?'} synced=${ds.policies.synced ?? 0} scope_missing=${!!ds.policies.scope_missing}}` : '')
          );
          console.debug('directory_sync (structured)', ds);
        }
      }

      const repoCount = summary.repositories_scanned || 0;
      const warnings = summary.warnings_count || 0;
      const headline = warnings > 0
        ? `⚠️ Evidence collected with ${warnings} warning${warnings === 1 ? '' : 's'}`
        : `✅ Evidence collected successfully!`;
      const repoLine = repoCount > 0
        ? `📊 Repositories scanned: ${repoCount}${summary.repository_name ? ` (first: ${summary.repository_name})` : ''}`
        : (techId === 'google-workspace'
          ? `👥 Users synced: ${summary.total_users || 0} · Groups: ${summary.groups_total || 0} · Admins: ${summary.admins || 0}`
          : techId === 'microsoft-365'
          ? `👥 Users: ${summary.total_users || 0} · Groups: ${summary.groups_total || 0} · Admins: ${summary.admins || 0}`
          : `📊 No repositories scanned — check token scope / permissions`);
      toast.success(
        `${headline}\n` +
        `${repoLine}\n` +
        (techId === 'google-workspace'
          ? `🌐 Mode: ${collectionMode}${collectionMode === 'mock' ? ' (NOT real data — see console)' : ''}\n` +
            `🏢 Domain: ${summary.customer_domain || '—'}\n`
          : techId === 'microsoft-365'
          ? `🔐 MFA registered: ${summary.mfa_registered_count ?? '—'} · Domains: ${summary.domains_total ?? '—'}\n` +
            (summary.posture_score != null ? `🛡️ Posture score: ${summary.posture_score}\n` : '')
          : `🏢 Organizations: ${summary.organizations_scanned || 0}\n` +
            `🔐 2FA: ${summary.two_factor_auth ? 'Enabled' : 'Disabled'}\n` +
            `⚠️ Risk Rating: ${summary.risk_rating || 'Unknown'}\n`) +
        `📋 Framework: ${summary.compliance_framework || 'multiple'}\n` +
        `Scroll down to view evidence history.`,
        { duration: 10000 }
      );

      // Reload evidence history
      await loadData();

      // Scroll to evidence history section
      setTimeout(() => {
        const evidenceSection = document.querySelector('.evidence-history-section');
        if (evidenceSection) {
          evidenceSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);

    } catch (error) {
      console.error('💥 Evidence collection threw:', {
        message: error?.message,
        name: error?.name,
        httpStatus: error?.response?.status,
        responseData: error?.response?.data,
        stack: error?.stack,
      });

      // Handle different error formats
      let errorMessage = 'Failed to collect evidence';

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg).join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setCollectingTechId(null);
      setCollectProgress(null);
      console.info(`⏱ total wall time: ${Math.round(performance.now() - debugStart)}ms`);
      console.groupEnd();
      /* eslint-enable no-console */
    }
  };

  const handleDisconnectClick = (tech) => {
    setTechToDisconnect(tech);
    setDisconnectDialogOpen(true);
  };

  const handleDisconnectConfirm = async () => {
    if (!techToDisconnect) return;
    
    try {
      const response = await axios.post(`${API}/api/technologies/disconnect`, {
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        // Connection rows are stored with underscores ('google_workspace')
        // even though the UI id uses hyphens ('google-workspace').
        technology: (techToDisconnect.id || '').replace(/-/g, '_')
      });
      
      // Honor the service's `success` flag — if the row couldn't be found
      // (e.g., already removed elsewhere), don't lie to the user with a
      // green success toast that leaves the card stuck on "Connected".
      if (response.data && response.data.success === false) {
        toast.error(response.data.message || `Could not disconnect ${techToDisconnect.name}. It may have already been removed — refreshing…`);
        setDisconnectDialogOpen(false);
        setTechToDisconnect(null);
        await loadData();
        return;
      }

      const evidenceDeleted = response.data.evidence_deleted || 0;
      
      toast.success(
        `${techToDisconnect.name} disconnected successfully! ${evidenceDeleted > 0 ? `\n${evidenceDeleted} evidence record(s) deleted.` : ''}`,
        { duration: 5000 }
      );
      
      setDisconnectDialogOpen(false);
      setTechToDisconnect(null);
      await loadData();
      
    } catch (error) {
      console.error('Disconnection failed:', error);
      toast.error('Failed to disconnect technology');
      setDisconnectDialogOpen(false);
      setTechToDisconnect(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg overflow-x-hidden">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg overflow-x-hidden">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Technology Connections</h1>
          <p className="text-slate-600">
            Connect your technology platforms to automatically collect compliance evidence
          </p>
        </div>

        {/* Available Technologies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {technologies.map((tech) => {
            const connected = isConnected(tech.id);

            // Microsoft 365 has its own detail page that hosts the connect
            // form, collection, posture and directory — so its card is a
            // simple entry point that routes there.
            if (tech.id === 'microsoft-365') {
              const status = getConnectionStatus(tech.id);
              return (
                <Card key={tech.id} className="hover:shadow-lg transition-shadow" data-testid="tech-card-microsoft-365">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connected ? 'bg-emerald-100 text-[#0f6cbd]' : 'bg-slate-100 text-slate-600'}`}>
                        {getTechIcon(tech.id)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{tech.name}</CardTitle>
                        {status === 'active' && (
                          <Badge className="bg-emerald-100 text-navy-700 mt-1" data-testid="status-active-microsoft-365">
                            <CheckCircle className="w-3 h-3 mr-1" /> Connected
                          </Badge>
                        )}
                        {status === 'pending_admin_approval' && (
                          <Badge className="bg-amber-100 text-amber-800 mt-1" data-testid="status-pending-microsoft-365">
                            <AlertCircle className="w-3 h-3 mr-1" /> Pending verification
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardDescription className="mt-3">{tech.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {status === 'active' ? (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleCollectEvidence('microsoft-365')}
                          disabled={!!collectingTechId}
                          className="w-full bg-navy-600 hover:bg-navy-700 text-white"
                          data-testid="collect-evidence-btn-microsoft-365"
                        >
                          {collectingTechId === 'microsoft-365' ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Collecting...
                            </>
                          ) : (
                            'Collect Evidence'
                          )}
                        </Button>

                        <Button
                          type="button"
                          onClick={() => navigate('/technologies/microsoft-365')}
                          variant="outline"
                          className="w-full inline-flex items-center justify-between"
                          data-testid="open-microsoft-365-btn"
                        >
                          Open Microsoft 365
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>

                        {collectingTechId === 'microsoft-365' && collectProgress && (
                          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="collect-evidence-progress-microsoft-365">
                            <div className="font-medium text-slate-800">
                              {collectProgress.message || collectProgress.step || 'Working…'}
                            </div>
                          </div>
                        )}

                        <Button
                          onClick={() => handleDisconnectClick(tech)}
                          variant="outline"
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          data-testid="disconnect-btn-microsoft-365"
                        >
                          Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleConnectClick(tech)}
                        className="w-full bg-navy-600 hover:bg-navy-700 text-white"
                        data-testid="open-microsoft-365-btn"
                      >
                        {status === 'pending_admin_approval' ? 'Complete Microsoft 365 setup' : 'Connect Microsoft 365'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={tech.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        connected ? 'bg-emerald-100 text-navy-600' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getTechIcon(tech.id)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{tech.name}</CardTitle>
                        {connected && getConnectionStatus(tech.id) === 'active' && (
                          <Badge className="bg-emerald-100 text-navy-700 mt-1" data-testid={`status-active-${tech.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Connected
                          </Badge>
                        )}
                        {connected && getConnectionStatus(tech.id) === 'active' && getScopesPendingReauth(tech.id) && (
                          <Badge className="bg-blue-100 text-blue-800 mt-1 ml-1" data-testid={`status-scopes-pending-${tech.id}`}>
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Re-authorize needed
                          </Badge>
                        )}
                        {connected && getConnectionStatus(tech.id) === 'pending_admin_approval' && (
                          <Badge className="bg-amber-100 text-amber-800 mt-1" data-testid={`status-pending-${tech.id}`}>
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Pending admin approval
                          </Badge>
                        )}
                        {tech.status === 'coming_soon' && (
                          <Badge className="bg-blue-100 text-blue-700 mt-1">
                            Coming Soon
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <CardDescription className="mt-3">{tech.description}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  {tech.status === 'available' && (
                    <>
                      {!connected ? (
                        <Button
                          onClick={() => handleConnectClick(tech)}
                          className="w-full bg-navy-600 hover:bg-navy-700 text-white"
                        >
                          Connect {tech.name}
                        </Button>
                      ) : getConnectionStatus(tech.id) === 'pending_admin_approval' ? (
                        <div className="space-y-2">
                          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <p className="font-medium mb-0.5">Awaiting Domain-Wide Delegation</p>
                            <p>
                              Your super admin needs to finish authorising Nova's Client ID in
                              the Google Admin Console. Click <em>Verify now</em> once they're done.
                            </p>
                            {getVerificationError(tech.id) && (
                              <p className="mt-1 text-[11px] text-amber-800 break-words" data-testid={`gw-verify-error-${tech.id}`}>
                                Last error: {getVerificationError(tech.id)}
                              </p>
                            )}
                          </div>
                          <Button
                            onClick={handleVerifyGoogleWorkspace}
                            disabled={gwVerifying}
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid={`verify-now-btn-${tech.id}`}
                          >
                            {gwVerifying ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Verifying...
                              </>
                            ) : (
                              'Verify now'
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDisconnectClick(tech)}
                            variant="outline"
                            className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Button
                            onClick={() => handleCollectEvidence(tech.id)}
                            disabled={!!collectingTechId}
                            className="w-full bg-navy-600 hover:bg-navy-700 text-white"
                            data-testid={`collect-evidence-btn-${tech.id}`}
                          >
                            {collectingTechId === tech.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Collecting...
                              </>
                            ) : (
                              'Collect Evidence'
                            )}
                          </Button>

                          {tech.id === 'google-workspace' && (
                            <>
                              <Button
                                type="button"
                                onClick={() => navigate('/technologies/google-workspace?tab=overview')}
                                variant="outline"
                                className="w-full inline-flex items-center justify-between"
                                data-testid="gw-open-dashboard-btn"
                              >
                                Open Workspace Dashboard
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                type="button"
                                onClick={openGwEditEmail}
                                variant="outline"
                                className="w-full"
                                data-testid="gw-edit-admin-email-btn"
                              >
                                Edit admin email
                              </Button>
                            </>
                          )}

                          {collectingTechId === tech.id && collectProgress && (
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="collect-evidence-progress">
                              <div className="font-medium text-slate-800">
                                {collectProgress.message || collectProgress.step || 'Working…'}
                              </div>
                              {typeof collectProgress.total_repos === 'number' && collectProgress.total_repos > 0 && (
                                <>
                                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-200">
                                    <div
                                      className="h-full bg-navy-600 transition-all duration-300"
                                      style={{
                                        width: `${Math.min(100, Math.round(((collectProgress.current_repo_index || 0) / collectProgress.total_repos) * 100))}%`
                                      }}
                                    />
                                  </div>
                                  <div className="mt-1 text-[11px] text-slate-500">
                                    {collectProgress.current_repo_index || 0} / {collectProgress.total_repos} repositories
                                    {collectProgress.current_repo_name ? ` · ${collectProgress.current_repo_name}` : ''}
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          <Button
                            onClick={() => handleDisconnectClick(tech)}
                            variant="outline"
                            className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          >
                            Disconnect
                          </Button>
                        </div>
                      )}
                      
                      {/* Evidence Summary - Show latest evidence for this technology */}
                      {connected && (() => {
                        // Find most recent evidence for this technology.
                        // Same hyphen-vs-underscore mismatch as `isConnected`.
                        const storageId = (tech.id || '').replace(/-/g, '_');
                        const techEvidence = evidenceHistory.find(
                          (ev) => ev.technology === tech.id || ev.technology === storageId
                        );

                        // Google Workspace gets its own domain-native
                        // summary rendering — Users / Groups / OUs / Admins
                        // / Findings + posture score + top GW findings.
                        if (storageId === 'google_workspace') {
                          if (!techEvidence && !gwOverview) return null;
                          const c = gwOverview?.counts || {};
                          const sev = gwOverview?.severity_counts || {};
                          const lastSnap = techEvidence?.collected_at
                            || gwOverview?.last_sync?.users
                            || gwOverview?.last_sync?.posture_evaluated_at;
                          const score = gwOverview?.score;
                          const grade = gwOverview?.grade;
                          const topFindings = gwOverview?.top_findings || [];
                          const sevColor = {
                            critical: 'bg-red-50 text-red-700 border-red-200',
                            high: 'bg-orange-50 text-orange-700 border-orange-200',
                            medium: 'bg-amber-50 text-amber-700 border-amber-200',
                            low: 'bg-slate-50 text-slate-700 border-slate-200',
                          };
                          const gradeColor = (g) => {
                            if (!g) return 'bg-slate-100 text-slate-600 border-slate-200';
                            const c0 = g[0];
                            if (c0 === 'A') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
                            if (c0 === 'B') return 'bg-navy-50 text-navy-700 border-navy-200';
                            if (c0 === 'C') return 'bg-amber-50 text-amber-700 border-amber-200';
                            if (c0 === 'D') return 'bg-orange-50 text-orange-700 border-orange-200';
                            return 'bg-red-50 text-red-700 border-red-200';
                          };
                          const fmtDate = (iso) => {
                            const d = parseServerDate(iso);
                            if (!d) return '—';
                            const pad = (n) => String(n).padStart(2, '0');
                            return `${pad(d.getDate())} ${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                          };
                          return (
                            <div className="mt-4 pt-4 border-t space-y-4" data-testid="gw-summary">
                              {/* Continuous-monitoring health (real customer signal) */}
                              <GwMonitoringHealth source="google_workspace" />

                              {/* Posture score + last snapshot */}
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-xs font-medium text-slate-600">Posture</span>
                                  {score != null ? (
                                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-md border ${gradeColor(grade)}`} data-testid="gw-posture-pill">
                                      {grade || '—'}
                                      <span className="text-[10px] font-normal opacity-80 tabular-nums">{Math.round(score)}/100</span>
                                    </span>
                                  ) : (
                                    <span className="text-xs text-slate-400">not evaluated</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Last snapshot</p>
                                  <p className="text-xs text-slate-700">{fmtDate(lastSnap)}</p>
                                </div>
                              </div>

                              {/* Domain-native stat tiles */}
                              <div className="grid grid-cols-3 gap-2">
                                <div className="bg-blue-50 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Users synced</p>
                                  <p className="text-lg font-bold text-blue-700 tabular-nums" data-testid="gw-stat-users">{c.users_synced ?? c.users ?? 0}</p>
                                </div>
                                <div className="bg-violet-50 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Groups</p>
                                  <p className="text-lg font-bold text-violet-700 tabular-nums" data-testid="gw-stat-groups">{c.groups ?? 0}</p>
                                </div>
                                <div className="bg-emerald-50 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Org Units</p>
                                  <p className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="gw-stat-org-units">{c.org_units ?? 0}</p>
                                </div>
                                <div className="bg-indigo-50 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Admins</p>
                                  <p className="text-lg font-bold text-indigo-700 tabular-nums" data-testid="gw-stat-admins">{c.admins ?? 0}</p>
                                </div>
                                <div className="bg-slate-100 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Policies evaluated</p>
                                  <p className="text-lg font-bold text-slate-700 tabular-nums" data-testid="gw-stat-rules">{c.findings ?? 0}</p>
                                </div>
                                <div className="bg-red-50 p-2.5 rounded-lg">
                                  <p className="text-[11px] text-slate-600 mb-0.5">Critical findings</p>
                                  <p className="text-lg font-bold text-red-700 tabular-nums" data-testid="gw-stat-critical">{sev.critical ?? 0}</p>
                                </div>
                              </div>

                              {/* Severity status bars */}
                              <div className="space-y-1.5">
                                <p className="text-xs font-medium text-slate-700">Findings by severity</p>
                                {['critical', 'high', 'medium', 'low'].map((s) => {
                                  const n = sev[s] || 0;
                                  const total = (sev.critical || 0) + (sev.high || 0) + (sev.medium || 0) + (sev.low || 0) + (sev.informational || 0);
                                  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                                  const barClass = {
                                    critical: 'bg-red-500',
                                    high: 'bg-orange-500',
                                    medium: 'bg-amber-500',
                                    low: 'bg-slate-400',
                                  }[s];
                                  return (
                                    <div key={s} className="flex items-center justify-between gap-2">
                                      <span className="text-xs text-slate-600 capitalize w-16">{s}</span>
                                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                        <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs font-semibold text-slate-700 w-6 text-right tabular-nums">{n}</span>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Key Findings */}
                              {topFindings.length > 0 && (
                                <div className="p-3 bg-slate-50 rounded-lg" data-testid="gw-key-findings">
                                  <p className="text-xs font-semibold text-slate-700 mb-2">Key Findings:</p>
                                  <ul className="space-y-1.5">
                                    {topFindings.slice(0, 3).map((f) => (
                                      <li key={f.rule_key} className="flex items-start gap-2 text-xs">
                                        <span className={`flex-shrink-0 inline-flex items-center text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border ${sevColor[f.severity] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                          {f.severity}
                                        </span>
                                        <div className="min-w-0">
                                          <p className="text-slate-800 font-medium truncate" title={f.label}>{f.label}</p>
                                          {f.what_would_fix && (
                                            <p className="text-[11px] text-slate-500 line-clamp-1" title={f.what_would_fix}>{f.what_would_fix}</p>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Secondary CTA — only renders when there's
                                  evidence to browse. Hide rather than route
                                  to an empty state so we don't ship a
                                  dead-end. */}
                              {techEvidence && (
                                <div className="space-y-2">
                                  <Button
                                    onClick={() => navigate('/technologies/google-workspace?tab=evidence')}
                                    variant="ghost"
                                    className="w-full text-slate-600 hover:bg-slate-50"
                                    data-testid="gw-view-evidence-btn"
                                  >
                                    Open Evidence Browser
                                  </Button>
                                  <Button
                                    onClick={() => navigate('/technologies/google-workspace/evidence')}
                                    variant="outline"
                                    className="w-full text-navy-600 border-emerald-200 hover:bg-emerald-50"
                                    data-testid="gw-view-evidence-details-btn"
                                  >
                                    View Evidence Details
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (!techEvidence) return null;

                        // Calculate summary stats
                        const repositories = techEvidence.data?.raw_data?.repositoryInformation?.repositories || [];
                        const totalRepos = repositories.length;
                        
                        let passedCount = 0;
                        let partialCount = 0;
                        let failedCount = 0;
                        
                        repositories.forEach(repo => {
                          const isPrivate = repo.private;
                          const has2FA = techEvidence.github_2fa;
                          const branchProtection = repo.branch_protection?.enabled;
                          const vulnerabilityAlerts = repo.vulnerability_alerts_enabled;
                          
                          let riskRating = 'Low';
                          if (!isPrivate || !has2FA) riskRating = 'High';
                          else if (!branchProtection || !vulnerabilityAlerts) riskRating = 'Medium';
                          
                          if (riskRating === 'High') failedCount++;
                          else if (riskRating === 'Medium') partialCount++;
                          else passedCount++;
                        });
                        
                        return (
                          <div className="mt-4 pt-4 border-t space-y-4">
                            {/* Collection Date */}
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-slate-600">Last Collection</p>
                              <p className="text-xs text-slate-700">
                                {(() => {
                                  const date = parseServerDate(techEvidence.collected_at);
                                  if (!date) return '—';
                                  const day = String(date.getDate()).padStart(2, '0');
                                  const month = date.toLocaleString('en-US', { month: 'short' });
                                  const year = date.getFullYear();
                                  const hours = String(date.getHours()).padStart(2, '0');
                                  const minutes = String(date.getMinutes()).padStart(2, '0');
                                  const seconds = String(date.getSeconds()).padStart(2, '0');
                                  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
                                })()}
                              </p>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-xs text-slate-600 mb-1">Repositories</p>
                                <p className="text-xl font-bold text-blue-700">{totalRepos}</p>
                              </div>
                              <div className="bg-purple-50 p-3 rounded-lg">
                                <p className="text-xs text-slate-600 mb-1">Organizations</p>
                                <p className="text-xl font-bold text-purple-700">
                                  {techEvidence.total_organizations || 0}
                                </p>
                              </div>
                            </div>

                            {/* Compliance Status */}
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-slate-700">Compliance Status</p>
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-600">Passed</span>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-emerald-500"
                                        style={{ width: `${totalRepos > 0 ? (passedCount / totalRepos) * 100 : 0}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-semibold text-navy-700 w-6 text-right">{passedCount}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-600">Partial</span>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-yellow-500"
                                        style={{ width: `${totalRepos > 0 ? (partialCount / totalRepos) * 100 : 0}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-semibold text-yellow-700 w-6 text-right">{partialCount}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-600">Failed</span>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-red-500"
                                        style={{ width: `${totalRepos > 0 ? (failedCount / totalRepos) * 100 : 0}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-semibold text-red-700 w-6 text-right">{failedCount}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Key Findings */}
                            {techEvidence.findings && techEvidence.findings.length > 0 && (
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs font-semibold text-slate-700 mb-2">Key Findings:</p>
                                <ul className="space-y-1">
                                  {techEvidence.findings.slice(0, 2).map((finding, idx) => (
                                    <li key={idx} className="text-xs text-slate-600 flex items-start">
                                      <span className="mr-1">•</span>
                                      <span>{finding}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* View Details Button */}
                            <Button
                              onClick={() => navigate(`/technologies/${techEvidence.technology}/evidence`)}
                              variant="outline"
                              className="w-full text-navy-600 border-emerald-200 hover:bg-emerald-50"
                            >
                              View Evidence Details
                            </Button>
                          </div>
                        );
                      })()}
                    </>
                  )}
                  
                  {tech.status === 'coming_soon' && (
                    <Button disabled className="w-full">
                      Coming Soon
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* No Evidence Message */}
        {evidenceHistory.length === 0 && userConnections.length > 0 && (
          <Card className="evidence-history-section">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Evidence Collected Yet</h3>
              <p className="text-slate-600 mb-4">
                Click "Collect Evidence" on a connected technology to start gathering compliance data
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* GitHub / Google Workspace Connection Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent
          className={`max-h-[90vh] overflow-y-auto ${
            // GW flow is dense — Step-by-step + Client ID readonly + scopes
            // list + 3-action footer. M365 is also dense (setup steps + 3
            // fields). GitHub is just a single PAT field, so keep it compact.
            (selectedTech?.id === 'google-workspace' || selectedTech?.id === 'microsoft-365')
              ? 'sm:max-w-2xl w-[calc(100vw-2rem)]'
              : 'sm:max-w-md'
          }`}
          data-testid={`connect-dialog-${selectedTech?.id || 'tech'}`}
        >
          <DialogHeader>
            <DialogTitle>{
              selectedTech?.id === 'google-workspace' ? 'Connect Google Workspace'
              : selectedTech?.id === 'microsoft-365' ? 'Connect Microsoft 365'
              : 'Connect GitHub'
            }</DialogTitle>
            <DialogDescription>
              {selectedTech?.id === 'google-workspace'
                ? 'Authorize read-only Directory access to enable compliance evidence collection'
                : selectedTech?.id === 'microsoft-365'
                ? 'Connect your Microsoft Entra ID tenant (read-only) to enable compliance evidence collection'
                : 'Enter your GitHub personal access token to enable compliance evidence collection'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedTech?.id === 'microsoft-365' ? (
              <>
                <p className="text-sm text-slate-600">
                  Read-only Microsoft Graph access using your own Entra ID app registration.
                  Grant admin consent for the read-only permissions, then paste your Tenant ID,
                  Client ID and Client Secret. The secret is stored encrypted.
                </p>

                <div>
                  <Label htmlFor="m365-tenant-id">Directory (Tenant) ID *</Label>
                  <Input
                    id="m365-tenant-id"
                    value={m365Form.tenant_id}
                    onChange={(e) => setM365Form({ ...m365Form, tenant_id: e.target.value.trim() })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="mt-1"
                    data-testid="m365-tenant-id-input"
                  />
                </div>
                <div>
                  <Label htmlFor="m365-client-id">Application (Client) ID *</Label>
                  <Input
                    id="m365-client-id"
                    value={m365Form.client_id}
                    onChange={(e) => setM365Form({ ...m365Form, client_id: e.target.value.trim() })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className="mt-1"
                    data-testid="m365-client-id-input"
                  />
                </div>
                <div>
                  <Label htmlFor="m365-client-secret">Client Secret value *</Label>
                  <Input
                    id="m365-client-secret"
                    type="password"
                    value={m365Form.client_secret}
                    onChange={(e) => setM365Form({ ...m365Form, client_secret: e.target.value })}
                    placeholder="Secret value (shown once at creation)"
                    className="mt-1"
                    data-testid="m365-client-secret-input"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Stored encrypted at rest. Never shown again after saving.</p>
                </div>

                <details className="rounded-md border border-slate-200 bg-slate-50" open={m365ShowSteps}>
                  <summary
                    className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800"
                    onClick={(e) => { e.preventDefault(); setM365ShowSteps((s) => !s); }}
                    data-testid="m365-steps-toggle"
                  >
                    How to set up (read-only)
                  </summary>
                  <div className="px-3 pb-3 space-y-3 text-sm">
                    <ol className="list-decimal ml-4 space-y-1 text-slate-600">
                      {(m365SetupInfo?.instructions || []).map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                    {(m365SetupInfo?.required_permissions || []).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-600 mb-1.5">Required application permissions</p>
                        <div className="flex flex-wrap gap-1.5">
                          {m365SetupInfo.required_permissions.map((p) => (
                            <span key={p} className="font-mono text-[11px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {m365SetupInfo?.notes && (
                      <p className="text-[12px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{m365SetupInfo.notes}</p>
                    )}
                    {m365SetupInfo?.portal_url && (
                      <a href={m365SetupInfo.portal_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-navy-600 hover:underline" data-testid="m365-portal-link">
                        Open Entra admin center <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </details>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={handleMicrosoft365Connect}
                    disabled={connecting}
                    className="flex-1 bg-navy-600 hover:bg-navy-700"
                    data-testid="m365-connect-verify-btn"
                  >
                    {connecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>) : 'Connect & Verify'}
                  </Button>
                  <Button
                    onClick={() => setConnectDialogOpen(false)}
                    variant="ghost"
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : selectedTech?.id === 'google-workspace' ? (
              <>
                <p className="text-sm text-slate-600">
                  Securely collect read-only Directory evidence from your Google Workspace
                  via Domain-Wide Delegation. Your super admin authorizes Nova's Client
                  ID once — no JSON keys are uploaded.
                </p>

                <div>
                  <Label htmlFor="gw-admin-email">Admin email to impersonate *</Label>
                  <Input
                    id="gw-admin-email"
                    type="email"
                    value={gwAdminEmail}
                    onChange={(e) => setGwAdminEmail(e.target.value)}
                    placeholder="admin@your-company.com"
                    className="mt-1"
                    data-testid="gw-admin-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="gw-customer-domain">Primary domain *</Label>
                  <Input
                    id="gw-customer-domain"
                    value={gwCustomerDomain}
                    onChange={(e) => setGwCustomerDomain(e.target.value)}
                    placeholder="your-company.com"
                    className="mt-1"
                    data-testid="gw-customer-domain-input"
                  />
                </div>

                <div>
                  <Label htmlFor="gw-notes">Notes (optional)</Label>
                  <Textarea
                    id="gw-notes"
                    value={gwNotes}
                    onChange={(e) => setGwNotes(e.target.value)}
                    placeholder="e.g. Waiting for IT to grant DWD by Friday"
                    rows={2}
                    className="mt-1"
                    data-testid="gw-notes-input"
                  />
                </div>

                <details className="group rounded border border-slate-200" open>
                  <summary className="cursor-pointer select-none list-none px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-between">
                    <span>Domain-Wide Delegation setup (super admin)</span>
                    <span className="text-xs text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-200">
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                      <p className="font-medium mb-1.5 text-blue-900">One-click admin handoff</p>
                      <p className="mb-2">
                        Send these details to your Google super admin (or open the Admin Console
                        yourself). They'll need to paste the Client ID + scopes on the
                        Domain-Wide Delegation page.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
                          onClick={() => {
                            window.open(
                              'https://admin.google.com/ac/owl/domainwidedelegation',
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                          data-testid="gw-open-admin-console-btn"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open Google Admin Console
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="inline-flex items-center gap-1"
                          disabled={!gwSetupInfo?.client_id}
                          onClick={async () => {
                            try {
                              const payload =
                                `Client ID: ${gwSetupInfo?.client_id || ''}\n\n` +
                                `OAuth Scopes (comma-separated):\n${(gwSetupInfo?.scopes || []).join(',')}`;
                              await navigator.clipboard.writeText(payload);
                              toast.success('Client ID + scopes copied to clipboard');
                            } catch (err) {
                              toast.error('Copy failed — please copy manually below');
                            }
                          }}
                          data-testid="gw-copy-dwd-payload-btn"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copy Client ID &amp; scopes
                        </Button>
                      </div>
                    </div>

                    <div className="text-sm text-slate-600">
                      <p className="font-medium mb-1 text-slate-800">Step-by-step</p>
                      <ol className="list-decimal ml-4 space-y-1">
                        {(gwSetupInfo?.instructions || []).map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    </div>

                    <div>
                      <Label className="text-xs">Nova Google Client ID</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          readOnly
                          value={gwSetupInfo?.client_id || '(set GOOGLE_WS_OAUTH_CLIENT_ID on server)'}
                          className="font-mono text-xs bg-slate-50 flex-1"
                          data-testid="gw-client-id"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!gwSetupInfo?.client_id}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(gwSetupInfo?.client_id || '');
                              toast.success('Client ID copied');
                            } catch (err) {
                              toast.error('Copy failed');
                            }
                          }}
                          data-testid="gw-copy-client-id-btn"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    <details className="group">
                      <summary className="cursor-pointer select-none list-none text-xs text-blue-600 hover:underline">
                        <span className="group-open:hidden">Show 7 read-only scopes</span>
                        <span className="hidden group-open:inline">Hide scopes</span>
                      </summary>
                      <Textarea
                        readOnly
                        value={(gwSetupInfo?.scopes || []).join(',\n')}
                        rows={5}
                        className="mt-2 font-mono text-[11px] bg-slate-50 resize-none"
                        data-testid="gw-scopes"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[11px] text-slate-500">
                          Paste comma-separated into the Domain-Wide Delegation page.
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px] inline-flex items-center gap-1"
                          disabled={!(gwSetupInfo?.scopes || []).length}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText((gwSetupInfo?.scopes || []).join(','));
                              toast.success('Scopes copied');
                            } catch (err) {
                              toast.error('Copy failed');
                            }
                          }}
                          data-testid="gw-copy-scopes-btn"
                        >
                          <Copy className="w-3 h-3" />
                          Copy scopes
                        </Button>
                      </div>
                    </details>
                  </div>
                </details>

                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <p className="font-medium text-slate-800 mb-1">Two ways to finish setup</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    <li><strong>Connect &amp; Verify</strong> — if your super admin has already authorised Nova in the Admin Console, we'll probe access and mark this connection as active immediately.</li>
                    <li><strong>Save for later</strong> — save the details now and verify once your admin completes Domain-Wide Delegation.</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={() => handleGoogleWorkspaceConnect(true)}
                    disabled={connecting}
                    className="flex-1 bg-navy-600 hover:bg-navy-700"
                    data-testid="gw-connect-verify-btn"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Connect & Verify'
                    )}
                  </Button>
                  <Button
                    onClick={() => handleGoogleWorkspaceConnect(false)}
                    disabled={connecting}
                    variant="outline"
                    className="flex-1"
                    data-testid="gw-save-pending-btn"
                  >
                    Save for later
                  </Button>
                  <Button
                    onClick={() => setConnectDialogOpen(false)}
                    variant="ghost"
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">How to get your GitHub token:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Go to GitHub Settings → Developer settings → Personal access tokens</li>
                    <li>Generate new token with 'repo' and 'read:org' scopes</li>
                    <li>Copy and paste the token below</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="github-token">GitHub Personal Access Token *</Label>
              <Input
                id="github-token"
                type="password"
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="github-username">GitHub Username (Optional)</Label>
              <Input
                id="github-username"
                value={githubUsername}
                onChange={(e) => setGithubUsername(e.target.value)}
                placeholder="your-github-username"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="github-org">GitHub Organization (Optional)</Label>
              <Input
                id="github-org"
                value={githubOrg}
                onChange={(e) => setGithubOrg(e.target.value)}
                placeholder="your-organization"
                className="mt-1"
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button
                onClick={handleGitHubConnect}
                disabled={connecting}
                className="flex-1 bg-navy-600 hover:bg-navy-700"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect GitHub'
                )}
              </Button>
              <Button
                onClick={() => setConnectDialogOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <span>Disconnect {techToDisconnect?.name}?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="text-slate-700">
                Are you sure you want to disconnect <strong>{techToDisconnect?.name}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">⚠️ Warning: This action cannot be undone</p>
                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                  <li>Your connection will be removed</li>
                  <li>All collected compliance evidence will be permanently deleted</li>
                  <li>You will need to reconnect and recollect evidence to use this integration again</li>
                </ul>
              </div>
              <p className="text-slate-600 text-sm">
                If you're experiencing issues, consider recollecting evidence instead of disconnecting.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnectConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit GW admin email Dialog */}
      <Dialog open={gwEditEmailOpen} onOpenChange={setGwEditEmailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="gw-edit-admin-email-dialog">
          <DialogHeader>
            <DialogTitle>Edit Google Workspace admin email</DialogTitle>
            <DialogDescription>
              This is the super-admin Nova impersonates via Domain-Wide Delegation.
              A typo here causes every Directory call to fail with{' '}
              <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">invalid_grant: Invalid email or User ID</code>.
              Update it without disconnecting — your monitoring schedules + evidence
              history are preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="gw-edit-email-input" className="text-xs">Admin email *</Label>
              <Input
                id="gw-edit-email-input"
                type="email"
                value={gwEditEmailValue}
                onChange={(e) => setGwEditEmailValue(e.target.value)}
                placeholder="admin@yourdomain.com"
                className="mt-1"
                data-testid="gw-edit-email-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Must be a real Workspace super admin in the connected tenant.
                After save, Nova re-runs the DWD probe; the result shows in a toast.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setGwEditEmailOpen(false)}
              disabled={gwEditEmailSaving}
              data-testid="gw-edit-email-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGwUpdateAdminEmail}
              disabled={gwEditEmailSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="gw-edit-email-save"
            >
              {gwEditEmailSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                'Save & verify'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechnologyConnections;
